import { AuthLayout, Button, Inline, InlineMessage, SSOButton, Spinner, Stack, Text } from '@olytic/foundation';
import { type ReactNode, useEffect, useState } from 'react';

import olyticMark from '../assets/olytic-inclined-ascent.svg';
import foundationBlocks from '../assets/foundation-hero-blocks.jpg';
import { useAuth } from './AuthProvider';
import { forbiddenText } from './session';

// Shared by every signed-out screen, so sign-in, "Not authorized" and the retry screen look alike.
const authFrame = {
  brand: (
    <Inline gap="sm" align="center">
      <img src={olyticMark} alt="" width={52} height={37} />
      <Text as="span" size="lg">
        Mothership
      </Text>
    </Inline>
  ),
  aside: <img src={foundationBlocks} alt="" />,
  asideBleed: true,
  footer: 'Powered by Olytic',
} as const;

/**
 * Renders the app only once the server has said this person may use it. Every
 * other state, including the one while that is being decided, is a screen of
 * its own, so nothing of the app shows before /api/me answers 200.
 */
export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const { state, signOut, retry } = useAuth();

  switch (state.status) {
    case 'loading':
      return (
        <div className="grid min-h-[100dvh] place-items-center">
          <Spinner label="Loading…" />
        </div>
      );
    case 'signed-out':
      return <SignInScreen notice={state.notice} />;
    case 'forbidden':
      return (
        <AuthLayout {...authFrame} title="Not authorized" description={forbiddenText(state.email, state.reason)}>
          <Button variant="outline" onClick={() => void signOut()}>
            Sign out
          </Button>
        </AuthLayout>
      );
    case 'error':
      return (
        <AuthLayout
          {...authFrame}
          title="Something went wrong"
          description={state.message}
        >
          <div className="flex flex-col gap-2">
            <Button variant="brand" onClick={retry}>
              Try again
            </Button>
            <Button variant="subtle" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </AuthLayout>
      );
    case 'authorized':
      return <>{children}</>;
  }
}

function SignInScreen({ notice }: { notice: string | undefined }): JSX.Element {
  const { signIn } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Back from Google's account chooser restores this page, still busy, from the back/forward cache.
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        setBusy(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  async function continueWithGoogle(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await signIn();
    } catch (signInError) {
      setError(
        signInError instanceof Error && signInError.message.length > 0
          ? signInError.message
          : 'We could not start sign-in. Please try again.',
      );
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      {...authFrame}
      title="Sign in"
      description="The internal workspace for Olytic's clients, projects and reporting."
    >
      <Stack gap="md">
        <SSOButton provider="google" loading={busy} onClick={() => void continueWithGoogle()} />
        <Text size="sm" tone="muted">
          Use your Olytic Google account. Access is limited to approved team members.
        </Text>
        {error === null ? null : (
          <InlineMessage tone="error" role="alert">
            {error}
          </InlineMessage>
        )}
        {notice === undefined || error !== null ? null : (
          <InlineMessage tone="warning" role="status">
            {notice}
          </InlineMessage>
        )}
      </Stack>
    </AuthLayout>
  );
}
